import { useEffect } from "react";
import { useDispatch } from "react-redux";
import * as Sentry from "@sentry/browser";
import {
  setBrowserOnline,
  setSocketStatus,
  setSocketReconnectAttempt,
} from "../store/actions/networkStateAction";

/**
 * 응시자 본인의 네트워크 끊김을 감지하는 훅.
 * - 브라우저 navigator.onLine + online/offline 이벤트
 * - Socket.IO connect / disconnect / connect_error / reconnect_attempt / reconnect / reconnect_failed
 *
 * Redux `networkState` 슬라이스에 상태를 기록하여 NetworkAlert UI가 구독한다.
 */
export const useNetworkMonitor = (socket) => {
  const dispatch = useDispatch();

  // 브라우저 online/offline 감지
  useEffect(() => {
    if (typeof window === "undefined") return;

    dispatch(setBrowserOnline(navigator.onLine));

    const handleOnline = () => {
      dispatch(setBrowserOnline(true));
      Sentry.captureMessage("[network] browser online");
    };
    const handleOffline = () => {
      dispatch(setBrowserOnline(false));
      Sentry.captureMessage("[network] browser offline");
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [dispatch]);

  // Socket.IO 연결 상태 감지
  useEffect(() => {
    if (!socket || !socket.io) return;

    const handleConnect = () => {
      dispatch(setSocketStatus("connected"));
      dispatch(setSocketReconnectAttempt(0));
    };
    const handleDisconnect = (reason) => {
      dispatch(setSocketStatus("disconnected"));
      Sentry.captureMessage(`[network] socket disconnect: ${reason}`);
    };
    const handleConnectError = (error) => {
      dispatch(setSocketStatus("connect_error"));
      Sentry.captureMessage(
        `[network] socket connect_error: ${error?.message || error}`
      );
    };
    const handleReconnectAttempt = (attempt) => {
      dispatch(setSocketStatus("reconnecting"));
      dispatch(setSocketReconnectAttempt(attempt));
    };
    const handleReconnect = (attempt) => {
      dispatch(setSocketStatus("connected"));
      dispatch(setSocketReconnectAttempt(0));
      Sentry.captureMessage(`[network] socket reconnected after ${attempt}`);
    };
    const handleReconnectFailed = () => {
      dispatch(setSocketStatus("reconnect_failed"));
      Sentry.captureMessage("[network] socket reconnect_failed");
    };

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on("connect_error", handleConnectError);
    socket.io.on("reconnect_attempt", handleReconnectAttempt);
    socket.io.on("reconnect", handleReconnect);
    socket.io.on("reconnect_failed", handleReconnectFailed);

    if (socket.connected) {
      dispatch(setSocketStatus("connected"));
    }

    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("connect_error", handleConnectError);
      socket.io.off("reconnect_attempt", handleReconnectAttempt);
      socket.io.off("reconnect", handleReconnect);
      socket.io.off("reconnect_failed", handleReconnectFailed);
    };
  }, [socket, dispatch]);
};
